import React, { useState, useRef, useEffect } from 'react';
import styles from './SensorDetailsPanel.module.scss';
import type { GeometryType, PointingMode, LightingCondition, SensorData } from '../../../types/sensor';
import { showToastMessage } from '../../../../../../utils/commonFunctions.js';
import { CloseSvg } from '../../../../../CommonComponents/svgs/Svgs';
import { CustomToolTip } from '../../LeftSideBar/Common';

export type { SensorData };

interface SensorDetailsPanelProps {
  onSave: (data: SensorData, editIndex?: number) => void | Promise<any>;
  onClose: () => void;
  mode?: 'add' | 'edit' | 'paste';
  initialData?: SensorData;
  pasteOptions?: SensorData[];
  onPreviewChange?: (partial: Partial<SensorData>) => void;
  saveLabel?: string;
  style?: React.CSSProperties;
  existingNames?: string[];
  existingSensors?: SensorData[];
}

const SensorIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
    <circle cx="12" cy="12" r="3" />
    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
  </svg>
);

const ChevronDown = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="m6 9 6 6 6-6" />
  </svg>
);

const CheckIcon = () => (
  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

const POINTING_MODE_LABELS: Record<PointingMode, string> = {
  az: 'Azimuth / Elevation',
  euler: 'Euler Angles',
  dcm: 'Body Axis',
  nadir: 'Nadir',
};

const LIGHTING_OPTIONS: { id: LightingCondition; label: string }[] = [
  { id: 'sunlit', label: 'Sunlit' },
  { id: 'eclipse', label: 'Eclipse' },
];

const SensorDetailsPanel: React.FC<SensorDetailsPanelProps> = ({ onSave, onClose, mode = 'add', initialData, pasteOptions = [], onPreviewChange, saveLabel, style, existingNames, existingSensors = [] }) => {
  const generateNextSensorName = (): string => {
    const sensorNames = existingSensors.map(s => s.name).filter(Boolean) as string[];
    let nextNumber = 1;
    while (sensorNames.some(name => name === `Sensor_${String(nextNumber).padStart(2, '0')}`)) {
      nextNumber++;
    }
    return `Sensor_${String(nextNumber).padStart(2, '0')}`;
  };

  const [name, setName] = useState(
    mode === 'add' && !initialData?.name ? generateNextSensorName() : (initialData?.name ?? '')
  );
  const [geometry, setGeometry] = useState<GeometryType>(initialData?.geometry ?? 'conical');
  const [fov, setFov] = useState(initialData?.fov ?? '30');
  const [fovH, setFovH] = useState(initialData?.fovH ?? '60');
  const [fovV, setFovV] = useState(initialData?.fovV ?? '40');
  const [pointingMode, setPointingMode] = useState<PointingMode>(initialData?.pointingMode ?? 'dcm');
  const [pointingMenuOpen, setPointingMenuOpen] = useState(false);
  const [azimuth, setAzimuth] = useState(initialData?.azimuth ?? '0');
  const [elevation, setElevation] = useState(initialData?.elevation ?? '90');
  const [yaw, setYaw] = useState(initialData?.yaw ?? '0');
  const [pitch, setPitch] = useState(initialData?.pitch ?? '0');
  const [roll, setRoll] = useState(initialData?.roll ?? '0');
  const [dcmX, setDcmX] = useState(initialData?.dcmX ?? '0');
  const [dcmY, setDcmY] = useState(initialData?.dcmY ?? '0');
  const [dcmZ, setDcmZ] = useState(initialData?.dcmZ ?? '0');

  const [lighting, setLighting] = useState<Record<LightingCondition, boolean>>(() => {
    const init: Record<LightingCondition, boolean> = { sunlit: true, eclipse: true, umbra: false, penumbra: false, all: false };
    if (initialData?.lighting) {
      initialData.lighting.forEach(cond => {
        if (cond in init) init[cond] = true;
      });
    }
    return init;
  });

  const [saving, setSaving] = useState(false);
  const lastToastTimeRef = useRef(0);
  const showThrottledInfoToast = (msg: string) => {
    const now = Date.now();
    if (now - lastToastTimeRef.current > 3000) {
      showToastMessage('info', msg);
      lastToastTimeRef.current = now;
    }
  };

  const [selectedPasteIndex, setSelectedPasteIndex] = useState(0);

  const preview = (patch: Partial<SensorData>) => {
    if (!onPreviewChange) return;
    const conds: LightingCondition[] = [];
    Object.entries(lighting).forEach(([key, val]) => {
      if (val) conds.push(key as LightingCondition);
    });
    onPreviewChange({
      name,
      geometry,
      fov,
      fovH,
      fovV,
      pointingMode,
      azimuth,
      elevation,
      yaw,
      pitch,
      roll,
      dcmX,
      dcmY,
      dcmZ,
      lighting: conds,
      ...patch,
    });
  };

  const handleToggleLighting = (id: LightingCondition) => {
    setLighting(prev => {
      const next = { ...prev, [id]: !prev[id] };
      const conds: LightingCondition[] = [];
      Object.entries(next).forEach(([key, val]) => {
        if (val) conds.push(key as LightingCondition);
      });
      if (onPreviewChange) {
        onPreviewChange({
          name,
          geometry,
          fov,
          fovH,
          fovV,
          pointingMode,
          azimuth,
          elevation,
          yaw,
          pitch,
          roll,
          dcmX,
          dcmY,
          dcmZ,
          lighting: conds,
        });
      }
      return next;
    });
  };

  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setPointingMenuOpen(false);
      }
    };
    if (pointingMenuOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [pointingMenuOpen]);

  useEffect(() => {
    if (mode === 'paste' && pasteOptions.length > 0) {
      const active = pasteOptions[selectedPasteIndex];
      if (active) {
        setName(active.name ?? '');
        setGeometry(active.geometry ?? 'conical');
        setFov(active.fov ?? '30');
        setFovH(active.fovH ?? '60');
        setFovV(active.fovV ?? '40');
        setPointingMode(active.pointingMode ?? 'dcm');
        setAzimuth(active.azimuth ?? '0');
        setElevation(active.elevation ?? '90');
        setYaw(active.yaw ?? '0');
        setPitch(active.pitch ?? '0');
        setRoll(active.roll ?? '0');
        setDcmX(active.dcmX ?? '0');
        setDcmY(active.dcmY ?? '0');
        setDcmZ(active.dcmZ ?? '0');
        const conds = active.lighting ?? [];
        setLighting({
          sunlit: conds.includes('sunlit'),
          eclipse: conds.includes('eclipse'),
          umbra: conds.includes('umbra'),
          penumbra: conds.includes('penumbra'),
          all: conds.includes('all'),
        });
      }
    }
  }, [selectedPasteIndex, mode, pasteOptions]);

  const handleFovChange = (
    value: string,
    setter: (v: string) => void,
    key: 'fov' | 'fovH' | 'fovV',
  ) => {
    let clean = value.replace(/[^0-9.]/g, '');
    const parts = clean.split('.');
    if (parts.length > 2) {
      clean = parts[0] + '.' + parts.slice(1).join('');
    }

    if (clean !== '') {
      const n = parseFloat(clean);
      if (!isNaN(n) && n > 150) {
        showThrottledInfoToast('FOV must be between 0 and 150');
        return;
      }
      if (!isNaN(n) && n <= 0) {
        showThrottledInfoToast('FOV must be greater than 0');
      }
    }

    setter(clean);
    preview({ [key]: clean });
  };

  const num = (v: string) => {
    const n = parseFloat(v);
    return !isNaN(n) && isFinite(n);
  };

  const validate = (): boolean => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      showToastMessage('info', 'Sensor name is required');
      return false;
    }
    if (
      existingNames &&
      existingNames.includes(trimmedName.toLowerCase()) &&
      trimmedName.toLowerCase() !== initialData?.name?.trim().toLowerCase()
    ) {
      showToastMessage('info', 'Sensor name already exists');
      return false;
    }

    if (geometry === 'conical') {
      const trimmed = fov.trim();
      if (!trimmed || !num(trimmed) || parseFloat(trimmed) <= 0 || parseFloat(trimmed) >= 180) {
        showToastMessage('info', 'FOV must be between 0 and 180 (exclusive)');
        return false;
      }
    } else {
      const trimmedH = fovH.trim();
      if (!trimmedH || !num(trimmedH) || parseFloat(trimmedH) <= 0 || parseFloat(trimmedH) >= 180) {
        showToastMessage('info', 'Horizontal FOV must be between 0 and 180 (exclusive)');
        return false;
      }
      const trimmedV = fovV.trim();
      if (!trimmedV || !num(trimmedV) || parseFloat(trimmedV) <= 0 || parseFloat(trimmedV) >= 180) {
        showToastMessage('info', 'Vertical FOV must be between 0 and 180 (exclusive)');
        return false;
      }
    }

    if (!lighting.sunlit && !lighting.eclipse) {
      showToastMessage('info', 'At least one lighting condition (Sunlit or Eclipse) is required');
      return false;
    }

    return true;
  };

  // Compute whether required fields are empty for button disabled state
  const isRequiredEmpty = (() => {
    if (!name.trim()) return true;
    if (!lighting.sunlit && !lighting.eclipse) return true;
    if (geometry === 'conical') {
      if (!fov.trim() || !num(fov.trim()) || parseFloat(fov.trim()) <= 0) return true;
    } else {
      if (!fovH.trim() || !num(fovH.trim()) || parseFloat(fovH.trim()) <= 0) return true;
      if (!fovV.trim() || !num(fovV.trim()) || parseFloat(fovV.trim()) <= 0) return true;
    }
    return false;
  })();

  const handleSave = async () => {
    if (!validate() || saving) return;
    const conds: LightingCondition[] = [];
    Object.entries(lighting).forEach(([key, val]) => {
      if (val) conds.push(key as LightingCondition);
    });
    setSaving(true);
    try {
      await onSave({
        sensorId: initialData?.sensorId,
        name,
        geometry,
        fov,
        fovH,
        fovV,
        pointingMode,
        azimuth,
        elevation,
        yaw,
        pitch,
        roll,
        dcmX,
        dcmY,
        dcmZ,
        lighting: conds,
      });
      onClose?.();
    } catch (e) {
      console.error('Failed to save sensor:', e);
    } finally {
      setSaving(false);
    }
  };

  const isDualFov = geometry === 'rectangular' || geometry === 'elliptical';

  return (
    <div className={styles.panel} style={style}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <SensorIcon />
          <span className={styles.headerTitle}>
            {mode === 'edit' ? 'EDIT SENSOR' : mode === 'paste' ? 'PASTE SENSOR' : 'SENSOR DETAILS'}
          </span>
        </div>
        <CustomToolTip title="Close">
          <button className={styles.closeBtn} onClick={onClose} type="button">
            <CloseSvg />
          </button>
        </CustomToolTip>
      </div>

      {/* Body */}
      <div className={styles.body}>
        {/* Paste selection dropdown */}
        {mode === 'paste' && pasteOptions.length > 1 && (
          <>
            <div className={styles.sectionHd}>Select Sensor to Paste</div>
            <div className={styles.fieldStack}>
              <select
                className={styles.selectInput}
                value={selectedPasteIndex}
                onChange={e => {
                  const idx = parseInt(e.target.value);
                  setSelectedPasteIndex(idx);
                }}
              >
                {pasteOptions.map((opt, i) => (
                  <option key={i} value={i}>
                    {opt.name || opt.geometry || `Sensor ${i + 1}`}
                  </option>
                ))}
              </select>
            </div>
          </>
        )}
        {/* Identification */}
        <div className={styles.sectionHd}>Sensor Name</div>
        <div className={styles.fieldStack}>
          <div className={styles.fieldInput}>
            <input
              value={name}
              maxLength={26}
              onChange={e => {
                let clean = e.target.value.replace(/[^a-zA-Z0-9 \-_]/g, '');
                if (clean.length > 26) {
                  clean = clean.slice(0, 26);
                  showThrottledInfoToast('Sensor name cannot exceed 26 characters');
                }
                if (e.target.value !== clean && e.target.value.length > clean.length) {
                  showThrottledInfoToast('Special characters are not allowed');
                }
                setName(clean);
                preview({ name: clean });
              }}
              placeholder="e.g. Sensor_01"
            />
          </div>
        </div>

        {/* Sensor Geometry */}
        <div className={styles.sectionHd}>Sensor Geometry</div>
        <div className={styles.geometryPills}>
          {(['conical', 'rectangular'] as GeometryType[]).map(g => (
            <div
              key={g}
              className={`${styles.geometryPill} ${geometry === g ? styles.geometryPillActive : ''}`}
              onClick={() => { setGeometry(g); preview({ geometry: g }); }}
              role="button"
            >
              {g.charAt(0).toUpperCase() + g.slice(1)}
            </div>
          ))}
        </div>

        {/* FOV */}
        <div className={styles.sectionHd}>Field of View</div>
        {!isDualFov ? (
          <div className={styles.fieldStack}>
            <span className={styles.fieldLabel}>FOV</span>
            <div className={styles.fieldInput}>
              <input
                value={fov}
                onChange={e => handleFovChange(e.target.value, setFov, 'fov')}
              />
              <span className={styles.suffix}>°</span>
            </div>
          </div>
        ) : (
          <>
            <div className={styles.fieldStack}>
              <span className={styles.fieldLabel}>Horizontal FOV</span>
              <div className={styles.fieldInput}>
                <input
                  value={fovH}
                  onChange={e => handleFovChange(e.target.value, setFovH, 'fovH')}
                />
                <span className={styles.suffix}>°</span>
              </div>
            </div>
            <div className={styles.fieldStack} style={{ marginTop: 8 }}>
              <span className={styles.fieldLabel}>Vertical FOV</span>
              <div className={styles.fieldInput}>
                <input
                  value={fovV}
                  onChange={e => handleFovChange(e.target.value, setFovV, 'fovV')}
                />
                <span className={styles.suffix}>°</span>
              </div>
            </div>
          </>
        )}

        {/* Pointing Axis */}
        <div style={{ opacity: 0.55, pointerEvents: 'none' }}>
          <div className={styles.pointingAxisRow}>
            <span className={styles.sectionHdInline}>Pointing Axis</span>
            <span className={styles.divider}>/</span>
            <div ref={menuRef} className={styles.pointingModeSwitcher}>
              <span>{POINTING_MODE_LABELS[pointingMode]}</span>
              <ChevronDown />
            </div>
            <div className={styles.dividerLine} />
          </div>

          {/* Pointing fields */}
          {pointingMode === 'az' && (
            <div className={styles.pointingFields}>
              <div className={styles.pointingRow}>
                <span className={styles.pointingLabel}>Azimuth</span>
                <div className={styles.fieldInput}><input value={azimuth} disabled /><span className={styles.suffix}>°</span></div>
              </div>
              <div className={styles.pointingRow}>
                <span className={styles.pointingLabel}>Elevation</span>
                <div className={styles.fieldInput}><input value={elevation} disabled /><span className={styles.suffix}>°</span></div>
              </div>
            </div>
          )}
          {pointingMode === 'euler' && (
            <div className={styles.pointingFields}>
              <div className={styles.pointingRow}>
                <span className={styles.pointingLabel}>Yaw</span>
                <div className={styles.fieldInput}><input value={yaw} disabled /><span className={styles.suffix}>°</span></div>
              </div>
              <div className={styles.pointingRow}>
                <span className={styles.pointingLabel}>Pitch</span>
                <div className={styles.fieldInput}><input value={pitch} disabled /><span className={styles.suffix}>°</span></div>
              </div>
              <div className={styles.pointingRow}>
                <span className={styles.pointingLabel}>Roll</span>
                <div className={styles.fieldInput}><input value={roll} disabled /><span className={styles.suffix}>°</span></div>
              </div>
            </div>
          )}
          {pointingMode === 'dcm' && (
            <div className={styles.pointingFields}>
              <div className={styles.pointingRow}>
                <span className={styles.pointingLabel}>X</span>
                <div className={styles.fieldInput}><input value={dcmX} disabled /></div>
              </div>
              <div className={styles.pointingRow}>
                <span className={styles.pointingLabel}>Y</span>
                <div className={styles.fieldInput}><input value={dcmY} disabled /></div>
              </div>
              <div className={styles.pointingRow}>
                <span className={styles.pointingLabel}>Z</span>
                <div className={styles.fieldInput}><input value={dcmZ} disabled /></div>
              </div>
            </div>
          )}
        </div>

        {/* Lighting Conditions */}
        <div className={styles.sectionHd}>Lighting Conditions</div>
        <div className={styles.lightingGrid}>
          {LIGHTING_OPTIONS.map(({ id, label }) => {
            const on = !!lighting[id];
            return (
              <div
                key={id}
                className={`${styles.lightingCb} ${on ? styles.lightingCbOn : ''}`}
                onClick={() => handleToggleLighting(id)}
                role="checkbox"
                aria-checked={on}
              >
                <span className={styles.cb}>
                  {on && <CheckIcon />}
                </span>
                {label}
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer */}
      <div className={styles.footer}>
        <button className={styles.btnOutline} type="button" onClick={onClose} disabled={saving}>Cancel</button>
        <button className={styles.btnPrimary} type="button" onClick={handleSave} disabled={saving || isRequiredEmpty}>
          {saving ? (mode === 'edit' ? 'Updating...' : 'Adding...') : (saveLabel ?? (mode === 'edit' ? 'Update Sensor' : mode === 'paste' ? 'Paste Sensor' : 'Add Sensor'))}
        </button>
      </div>
    </div>
  );
};

export default SensorDetailsPanel;
